import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store/store";
import DashboardLayout from "./DashboardLayout";
import { addOfflineSubmissionByCategory } from "../store/stateSlice.ts";
import { 
    collectEstimationDataCycleSave, 
    saveSectionApiCall, 
    fetchCycleData
} from "../Services/MixingParameterVerification.ts";
import { 
    addOfflineData, 
    setFetchedCycles, 
    selectFetchedCycles,
    type ProcessParameterVerificationCycleData 
} from "../store/MixingParameterVerificationSlice.ts";

const MixingParameterVerification: React.FC = () => {
    console.log('ProcessParameterVerification component is rendering...');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Redux state
    const plantTourId = useSelector((state: RootState) => state.planTour.plantTourId);
    const selectedCycle = useSelector((state: RootState) => state.planTour.selectedCycle);
    const user = useSelector((state: any) => state.user.user);
    const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);
    const offlineSubmissionsByCategory = useSelector((state: RootState) => state.appState.offlineSubmissionsByCategory);
    const fetchedCycles = useSelector(selectFetchedCycles);
    
    // Local state
    const [currentCycle, setCurrentCycle] = useState(1);
    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState<{ [key: number]: boolean }>({});
    const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<string>>(new Set());
    // const [isLoadingCycles, setIsLoadingCycles] = useState(false);
    const [completedCycles, setCompletedCycles] = useState<Set<number>>(new Set());
    const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
    const [bakingTimeManual, setBakingTimeManual] = useState<string>('');
    type CycleProductData = {
        productName: string;
        batchNo: string;
        category: string;
        lineNo: string;
        executiveName: string;
        sku: string;
        parameters: {
            [key: string]: {
                standardValue: string;
                actualValue: string;
                actionTaken: string;
            };
        };
    };
    const [cycleData, setCycleData] = useState<{ [key: number]: CycleProductData }>({});
    
    // Process Parameter state - each parameter has standardValue, actualValue, and actionTaken
    const [processParameters, setProcessParameters] = useState<{
        [key: string]: {
            standardValue: string;
            actualValue: string;
            actionTaken: string;
        };
    }>({
        // Premixing - Ingredient Temperature °C
        rpoSolidFat: { standardValue: '', actualValue: '', actionTaken: '' },
        invertSyrup: { standardValue: '', actualValue: '', actionTaken: '' },
        butter: { standardValue: '', actualValue: '', actionTaken: '' },
        blackjack: { standardValue: '', actualValue: '', actionTaken: '' },
        water: { standardValue: '', actualValue: '', actionTaken: '' },
        // Mixing Parameters
        creamingTime: { standardValue: '', actualValue: '', actionTaken: '' },
        mixingTime: { standardValue: '', actualValue: '', actionTaken: '' },
        doughTemperature: { standardValue: '', actualValue: '', actionTaken: '' },
        standingTime: { standardValue: '', actualValue: '', actionTaken: '' },
        // Biscuit Parameters
        biscuitAppearance: { standardValue: '', actualValue: '', actionTaken: '' },
        stackHeight: { standardValue: '', actualValue: '', actionTaken: '' },
        dimensions: { standardValue: '', actualValue: '', actionTaken: '' },
        // Forming
        moulderRpm: { standardValue: '', actualValue: '', actionTaken: '' },
        sugarSaltCashewSprinkling: { standardValue: '', actualValue: '', actionTaken: '' },
        // Sieving
        ingredientsOfBatch: { standardValue: '', actualValue: '', actionTaken: '' },
        // Moisture Content
        moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
        // Dry Biscuit weight
        dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
    });

    
    // Start form data
    const [startFormData, setStartFormData] = useState({
        productName: '',
        batchNo: '',
        category: 'Select',
        lineNo: '',
        executiveName: '',
        sku: ''
    });

    const handleStartSession = async () => {
        console.log('Starting Process Parameter Verification session...');

        try {
            // Ensure processParameters is properly initialized with all fields
            setProcessParameters(prev => {
                // Check if all required fields exist
                const requiredFields = [
                    'rpoSolidFat', 'invertSyrup', 'butter', 'blackjack', 'water',
                    'creamingTime', 'mixingTime', 'doughTemperature', 'standingTime',
                    'biscuitAppearance', 'stackHeight', 'dimensions', 'moulderRpm',
                    'sugarSaltCashewSprinkling', 'ingredientsOfBatch',
                    'moistureContent', 'dryBiscuitWeight'
                ];
                
                const hasAllFields = requiredFields.every(field => 
                    prev[field] && 
                    typeof prev[field] === 'object' && 
                    'standardValue' in prev[field] &&
                    'actualValue' in prev[field] &&
                    'actionTaken' in prev[field]
                );
                
                if (hasAllFields) {
                    return prev;
                }
                
                // Initialize missing fields
                return {
                    rpoSolidFat: prev.rpoSolidFat || { standardValue: '', actualValue: '', actionTaken: '' },
                    invertSyrup: prev.invertSyrup || { standardValue: '', actualValue: '', actionTaken: '' },
                    butter: prev.butter || { standardValue: '', actualValue: '', actionTaken: '' },
                    blackjack: prev.blackjack || { standardValue: '', actualValue: '', actionTaken: '' },
                    water: prev.water || { standardValue: '', actualValue: '', actionTaken: '' },
                    creamingTime: prev.creamingTime || { standardValue: '', actualValue: '', actionTaken: '' },
                    mixingTime: prev.mixingTime || { standardValue: '', actualValue: '', actionTaken: '' },
                    doughTemperature: prev.doughTemperature || { standardValue: '', actualValue: '', actionTaken: '' },
                    standingTime: prev.standingTime || { standardValue: '', actualValue: '', actionTaken: '' },
                    biscuitAppearance: prev.biscuitAppearance || { standardValue: '', actualValue: '', actionTaken: '' },
                    stackHeight: prev.stackHeight || { standardValue: '', actualValue: '', actionTaken: '' },
                    dimensions: prev.dimensions || { standardValue: '', actualValue: '', actionTaken: '' },
                    moulderRpm: prev.moulderRpm || { standardValue: '', actualValue: '', actionTaken: '' },
                    sugarSaltCashewSprinkling: prev.sugarSaltCashewSprinkling || { standardValue: '', actualValue: '', actionTaken: '' },
                    ingredientsOfBatch: prev.ingredientsOfBatch || { standardValue: '', actualValue: '', actionTaken: '' },
                    moistureContent: prev.moistureContent || { standardValue: '', actualValue: '', actionTaken: '' },
                    dryBiscuitWeight: prev.dryBiscuitWeight || { standardValue: '', actualValue: '', actionTaken: '' }
                };
            });
            
            // Store start data in localStorage
            const storageKey = `process-parameter-cycle-${currentCycle}-start-data`;
            const startTime = new Date();
            localStorage.setItem(storageKey, JSON.stringify({ ...startFormData, bakingStartTime: startTime.toISOString() }));
            sessionStorage.setItem("shiftValue", selectedCycle || "shift 1");
            
            const defaultTimeStr = `${String(startTime.getMinutes()).padStart(2, '0')}:${String(startTime.getSeconds()).padStart(2, '0')}`;
            setSessionStartTime(startTime);
            setBakingTimeManual(defaultTimeStr);
            setIsSessionStarted(true);
            setIsExpanded(prev => ({ ...prev, [currentCycle]: true }));
        } catch (error) {
            console.error('Error starting session:', error);
            alert('Error starting session. Please try again.');
        }
    };

    const handleParameterChange = (field: string, subField: 'standardValue' | 'actualValue' | 'actionTaken', value: string) => {
        setProcessParameters(prev => {
            // Ensure the field exists before accessing it
            const currentField = prev[field] || { standardValue: '', actualValue: '', actionTaken: '' };
            return {
                ...prev,
                [field]: {
                    ...currentField,
                    [subField]: value
                }
            };
        });
    };

    /** Format time input to MM:SS (minutes:seconds) on blur */
    const formatTimeMMSS = (value: string): string => {
        if (!value || !value.trim()) return value;
        const v = value.trim();
        if (/^\d{1,2}:\d{1,2}$/.test(v)) {
            const [m, s] = v.split(':').map(x => x.padStart(2, '0'));
            return `${m}:${s}`;
        }
        if (/^\d{3,4}$/.test(v)) {
            const mins = v.slice(0, -2).padStart(2, '0');
            const secs = v.slice(-2).padStart(2, '0');
            return `${mins}:${secs}`;
        }
        return value;
    };

    /** Live format: only allow MM:SS. Typing "33333" becomes "33:33" automatically. */
    const formatTimeInputLive = (value: string): string => {
        const digits = value.replace(/\D/g, '');
        if (digits.length === 0) return '';
        if (digits.length === 1) return digits;
        if (digits.length === 2) return digits + ':';
        if (digits.length === 3) return digits.slice(0, 2) + ':' + digits[2];
        return digits.slice(0, 2) + ':' + digits.slice(2, 4);
    };

    const handleSaveSession = async () => {
        // Validate that at least one field has data
        const hasData = Object.values(processParameters).some(param => 
            param.standardValue.trim() !== '' || 
            param.actualValue.trim() !== '' || 
            param.actionTaken.trim() !== ''
        );

        if (!hasData) {
            alert('Please enter at least one parameter before saving');
            return;
        }

        if (!plantTourId) {
            alert('Plant Tour ID is missing. Please start a plant tour first.');
            return;
        }

        try {
            console.log('Saving Process Parameter Verification session...');
            console.log('Cycle:', currentCycle);
            console.log('Start Form Data:', startFormData);
            console.log('Process Parameters:', processParameters);
            console.log('Is Offline:', isOfflineStarted);
            
            const userName = user?.Name || 'Current User';
            
            // Check if offline mode
            if (isOfflineStarted) {
                console.log('Saving Process Parameter Verification data in offline mode to Redux...');
                
                // Build records for offline storage using service function
                const { savedData } = await collectEstimationDataCycleSave(
                    currentCycle,
                    startFormData,
                    processParameters as any,
                    plantTourId || '',
                    userName,
                    selectedCycle
                );
                
                const record = savedData[0];
                
                // Create offline submission
                const offlineSubmission = {
                    cycleNo: currentCycle,
                    records: [record],
                    timestamp: Date.now(),
                    plantTourId: plantTourId || ''
                };
                
                // Store in Redux slice for offline data management
                dispatch(addOfflineData(offlineSubmission));
                
                // Also store in offlineSubmissionsByCategory for sync
                dispatch(addOfflineSubmissionByCategory({ 
                    category: 'Process Parameter Verification', 
                    submission: offlineSubmission 
                }));
                
                // Save cycle data locally for display (append - multiple products per cycle)
                const savedCycleData: CycleProductData = {
                    productName: startFormData.productName,
                    batchNo: startFormData.batchNo || '',
                    category: startFormData.category || '',
                    lineNo: startFormData.lineNo || '',
                    executiveName: startFormData.executiveName,
                    sku: startFormData.sku || '',
                    parameters: { ...processParameters }
                };
                
                setCompletedCycles(prev => new Set([...prev, currentCycle]));
                setCycleData(prev => ({ ...prev, [currentCycle]: savedCycleData }));
                
                const storageKey = `process-parameter-cycle-${currentCycle}-start-data`;
                localStorage.removeItem(storageKey);
                
                console.log('Process Parameter Verification data saved to Redux. Total offline submissions:', (offlineSubmissionsByCategory['Process Parameter Verification'] || []).length + 1);
                
                setBakingTimeManual('');
                setStartFormData({
                    productName: '',
                    batchNo: '',
                    category: 'Select',
                    lineNo: '',
                    executiveName: '',
                    sku: ''
                });
                setProcessParameters({
                    rpoSolidFat: { standardValue: '', actualValue: '', actionTaken: '' },
                    invertSyrup: { standardValue: '', actualValue: '', actionTaken: '' },
                    butter: { standardValue: '', actualValue: '', actionTaken: '' },
                    blackjack: { standardValue: '', actualValue: '', actionTaken: '' },
                    water: { standardValue: '', actualValue: '', actionTaken: '' },
                    creamingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    mixingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    doughTemperature: { standardValue: '', actualValue: '', actionTaken: '' },
                    standingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    biscuitAppearance: { standardValue: '', actualValue: '', actionTaken: '' },
                    stackHeight: { standardValue: '', actualValue: '', actionTaken: '' },
                    dimensions: { standardValue: '', actualValue: '', actionTaken: '' },
                    moulderRpm: { standardValue: '', actualValue: '', actionTaken: '' },
                    sugarSaltCashewSprinkling: { standardValue: '', actualValue: '', actionTaken: '' },
                    ingredientsOfBatch: { standardValue: '', actualValue: '', actionTaken: '' },
                    moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
                    dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
                });
                setIsSessionStarted(false);
                setCurrentCycle(prev => Math.min(prev + 1, totalCycles));
                alert(`Cycle ${currentCycle} completed. Moving to Cycle ${currentCycle + 1}.`);
                return; // Exit early for offline mode
            } else {
                // Online mode - Use service functions
                console.log('Saving Process Parameter Verification data in online mode...');
                
                const { savedData } = await collectEstimationDataCycleSave(
                    currentCycle,
                    startFormData,
                    processParameters as any,
                    plantTourId || '',
                    userName,
                    selectedCycle
                );
                
                const result = await saveSectionApiCall(savedData);
                
                if (!result.success) {
                    throw new Error(result.message || 'Save failed');
                }
                
                const newEntry: ProcessParameterVerificationCycleData = {
                    cycleNum: currentCycle.toString(),
                    product: startFormData.productName,
                    executiveName: startFormData.executiveName,
                    parameters: {
                        rpoSolidFat: processParameters.rpoSolidFat.actualValue || '',
                        invertSyrup: processParameters.invertSyrup.actualValue || '',
                        butter: processParameters.butter.actualValue || '',
                        blackjack: processParameters.blackjack.actualValue || '',
                        water: processParameters.water.actualValue || '',
                        creamingTime: processParameters.creamingTime.actualValue || '',
                        mixingTime: processParameters.mixingTime.actualValue || '',
                        doughTemperature: processParameters.doughTemperature.actualValue || '',
                        standingTime: processParameters.standingTime.actualValue || '',
                        biscuitAppearance: processParameters.biscuitAppearance.actualValue || '',
                        stackHeight: processParameters.stackHeight.actualValue || '',
                        dimensions: processParameters.dimensions.actualValue || '',
                        moulderRpm: processParameters.moulderRpm.actualValue || '',
                        sugarSaltCashewSprinkling: processParameters.sugarSaltCashewSprinkling.actualValue || '',
                        ingredientsOfBatch: processParameters.ingredientsOfBatch.actualValue || ''
                    }
                };
                const existingWithoutCurrent = (fetchedCycles || []).filter((c: any) => c.cycleNum !== currentCycle.toString());
                dispatch(setFetchedCycles([...existingWithoutCurrent, newEntry]));
                
                const savedCycleData: CycleProductData = {
                    productName: startFormData.productName,
                    batchNo: startFormData.batchNo || '',
                    category: startFormData.category || '',
                    lineNo: startFormData.lineNo || '',
                    executiveName: startFormData.executiveName,
                    sku: startFormData.sku || '',
                    parameters: { ...processParameters }
                };
                
                setCompletedCycles(prev => new Set([...prev, currentCycle]));
                setCycleData(prev => ({ ...prev, [currentCycle]: savedCycleData }));
                
                const storageKey = `process-parameter-cycle-${currentCycle}-start-data`;
                localStorage.removeItem(storageKey);
                
                setBakingTimeManual('');
                setStartFormData({
                    productName: '',
                    batchNo: '',
                    category: 'Select',
                    lineNo: '',
                    executiveName: '',
                    sku: ''
                });
                setProcessParameters({
                    rpoSolidFat: { standardValue: '', actualValue: '', actionTaken: '' },
                    invertSyrup: { standardValue: '', actualValue: '', actionTaken: '' },
                    butter: { standardValue: '', actualValue: '', actionTaken: '' },
                    blackjack: { standardValue: '', actualValue: '', actionTaken: '' },
                    water: { standardValue: '', actualValue: '', actionTaken: '' },
                    creamingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    mixingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    doughTemperature: { standardValue: '', actualValue: '', actionTaken: '' },
                    standingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                    biscuitAppearance: { standardValue: '', actualValue: '', actionTaken: '' },
                    stackHeight: { standardValue: '', actualValue: '', actionTaken: '' },
                    dimensions: { standardValue: '', actualValue: '', actionTaken: '' },
                    moulderRpm: { standardValue: '', actualValue: '', actionTaken: '' },
                    sugarSaltCashewSprinkling: { standardValue: '', actualValue: '', actionTaken: '' },
                    ingredientsOfBatch: { standardValue: '', actualValue: '', actionTaken: '' },
                    moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
                    dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
                });
                setIsSessionStarted(false);
                setCurrentCycle(prev => Math.min(prev + 1, totalCycles));
                alert(`Cycle ${currentCycle} completed. Moving to Cycle ${currentCycle + 1}.`);
            }
        } catch (error) {
            console.error('Error saving session:', error);
            alert(error instanceof Error ? error.message : 'Error saving session. Please try again.');
        }
    };

    const totalCycles = 8;

    const handleCancel = () => {
        if (window.confirm('Are you sure you want to cancel? All unsaved data will be lost.')) {
            setIsSessionStarted(false);
            setBakingTimeManual('');
            setProcessParameters({
                rpoSolidFat: { standardValue: '', actualValue: '', actionTaken: '' },
                invertSyrup: { standardValue: '', actualValue: '', actionTaken: '' },
                butter: { standardValue: '', actualValue: '', actionTaken: '' },
                blackjack: { standardValue: '', actualValue: '', actionTaken: '' },
                water: { standardValue: '', actualValue: '', actionTaken: '' },
                creamingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                mixingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                doughTemperature: { standardValue: '', actualValue: '', actionTaken: '' },
                standingTime: { standardValue: '', actualValue: '', actionTaken: '' },
                biscuitAppearance: { standardValue: '', actualValue: '', actionTaken: '' },
                stackHeight: { standardValue: '', actualValue: '', actionTaken: '' },
                dimensions: { standardValue: '', actualValue: '', actionTaken: '' },
                moulderRpm: { standardValue: '', actualValue: '', actionTaken: '' },
                sugarSaltCashewSprinkling: { standardValue: '', actualValue: '', actionTaken: '' },
                ingredientsOfBatch: { standardValue: '', actualValue: '', actionTaken: '' },
                moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
                dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
            });
        }
    };

    const toggleCompletedCycleExpansion = (cycleNum: string) => {
        setExpandedCompletedCycles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cycleNum)) {
                newSet.delete(cycleNum);
            } else {
                newSet.add(cycleNum);
            }
            return newSet;
        });
    };

    // Fetch and display cycles from API (similar to QualityWallRecord)
    const fetchAndDisplayCycles = async () => {
        if (!plantTourId || isOfflineStarted) {
            return;
        }

        try {
            // setIsLoadingCycles(true);
            console.log('Fetching Process Parameter Verification cycles...');
            const fetchedData = await fetchCycleData(plantTourId);
            console.log('Fetched cycles:', fetchedData);
            console.log('Number of cycles fetched:', fetchedData?.length || 0);

            if (fetchedData && fetchedData.length > 0) {
                // Update Redux with fetched cycles
                const cyclesForRedux: ProcessParameterVerificationCycleData[] = fetchedData.map((cycle: any) => ({
                    cycleNum: cycle.cycleNum,
                    product: cycle.product || 'N/A',
                    executiveName: cycle.executiveName || 'N/A',
                    parameters: {
                        rpoSolidFat: cycle.cr3ea_premixingingredienttemperaturesolidfat || '',
                        invertSyrup: cycle.cr3ea_premixingingredienttemperatureinvertsyrup || '',
                        butter: cycle.cr3ea_premixingingredienttemperaturebutter || '',
                        blackjack: cycle.cr3ea_premixingingredienttemperatureblackjack || '',
                        water: cycle.cr3ea_premixingingredienttemperaturewater || '',
                        creamingTime: cycle.cr3ea_mixingparametercreamingtime || '',
                        mixingTime: cycle.cr3ea_mixingparametermixingtime || '',
                        doughTemperature: cycle.cr3ea_mixingparameterdoughtemptime || '',
                        standingTime: cycle.cr3ea_mixingparameterstandingtime || '',
                        biscuitAppearance: cycle.cr3ea_biscuitparameterbiscuitappearancetime || '',
                        stackHeight: cycle.cr3ea_biscuitparameterstackheighttime || '',
                        dimensions: cycle.cr3ea_biscuitparameterdimensions || '',
                        moulderRpm: cycle.cr3ea_formingmoulderrpmstokestime || '',
                        sugarSaltCashewSprinkling: cycle.cr3ea_formingsprinklingtime || '',
                        ingredientsOfBatch: cycle.cr3ea_sievingingredientsofbatch || ''
                    }
                }));
                dispatch(setFetchedCycles(cyclesForRedux));

                const completedSet = new Set<number>();
                const cycleDataMap: { [key: number]: CycleProductData } = {};

                fetchedData.forEach((cycle: any) => {
                    const cycleNum = parseInt(cycle.cycleNum);
                    if (cycleNum && !isNaN(cycleNum)) {
                        const parameters: { [key: string]: { standardValue: string; actualValue: string; actionTaken: string } } = {
                            rpoSolidFat: { standardValue: cycle.cr3ea_rposolidfat_standard || '', actualValue: cycle.cr3ea_rposolidfat_temperature || '', actionTaken: cycle.cr3ea_rposolidfat_action || '' },
                            invertSyrup: { standardValue: cycle.cr3ea_invertsyrup_standard || '', actualValue: cycle.cr3ea_invertsyrup_temperature || '', actionTaken: cycle.cr3ea_invertsyrup_action || '' },
                            butter: { standardValue: cycle.cr3ea_butter_standard || '', actualValue: cycle.cr3ea_butter_temperature || '', actionTaken: cycle.cr3ea_butter_action || '' },
                            blackjack: { standardValue: cycle.cr3ea_blackjack_standard || '', actualValue: cycle.cr3ea_blackjack_temperature || '', actionTaken: cycle.cr3ea_blackjack_action || '' },
                            water: { standardValue: cycle.cr3ea_water_standard || '', actualValue: cycle.cr3ea_water_temperature || '', actionTaken: cycle.cr3ea_water_action || '' },
                            creamingTime: { standardValue: cycle.cr3ea_creamingtime_standard || '', actualValue: cycle.cr3ea_creamingtime_temperature || '', actionTaken: cycle.cr3ea_creamingtime_action || '' },
                            mixingTime: { standardValue: cycle.cr3ea_mixingtime_standard || '', actualValue: cycle.cr3ea_mixingtime_temperature || '', actionTaken: cycle.cr3ea_mixingtime_action || '' },
                            doughTemperature: { standardValue: cycle.cr3ea_doughtemperature_standard || '', actualValue: cycle.cr3ea_doughtemperature_temperature || '', actionTaken: cycle.cr3ea_doughtemperature_action || '' },
                            standingTime: { standardValue: cycle.cr3ea_standingtime_standard || '', actualValue: cycle.cr3ea_standingtime_temperature || '', actionTaken: cycle.cr3ea_standingtime_action || '' },
                            biscuitAppearance: { standardValue: cycle.cr3ea_biscuitappearance_standard || '', actualValue: cycle.cr3ea_biscuitappearance_temperature || '', actionTaken: cycle.cr3ea_biscuitappearance_action || '' },
                            stackHeight: { standardValue: cycle.cr3ea_stackheightspecifiednobiscuits_standard || '', actualValue: cycle.cr3ea_stackheightspecifiednobiscuits_temperature || '', actionTaken: cycle.cr3ea_stackheightspecifiednobiscuits_action || '' },
                            dimensions: { standardValue: cycle.cr3ea_dimensionsatpackingtable_standard || '', actualValue: cycle.cr3ea_dimensionsatpackingtable_temperature || '', actionTaken: cycle.cr3ea_dimensionsatpackingtable_action || '' },
                            moulderRpm: { standardValue: cycle.cr3ea_moulderrpmstokes_standard || '', actualValue: cycle.cr3ea_moulderrpmstokes_temperature || '', actionTaken: cycle.cr3ea_moulderrpmstokes_action || '' },
                            sugarSaltCashewSprinkling: { standardValue: cycle.cr3ea_sugarsaltcashewsprinkling_standard || '', actualValue: cycle.cr3ea_sugarsaltcashewsprinkling_temperature || '', actionTaken: cycle.cr3ea_sugarsaltcashewsprinkling_action || '' },
                            ingredientsOfBatch: { standardValue: cycle.cr3ea_ingredientsofbatch_standard || '', actualValue: cycle.cr3ea_ingredientsofbatch_temperature || '', actionTaken: cycle.cr3ea_ingredientsofbatch_action || '' },
                            moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
                            dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
                        };
                        const savedCycleData: CycleProductData = {
                            productName: cycle.product || 'N/A',
                            batchNo: '',
                            category: '',
                            lineNo: '',
                            executiveName: cycle.executiveName || 'N/A',
                            sku: '',
                            parameters
                        };
                        completedSet.add(cycleNum);
                        cycleDataMap[cycleNum] = savedCycleData;
                    }
                });

                console.log(`Setting ${completedSet.size} completed cycles:`, Array.from(completedSet));
                setCompletedCycles(completedSet);
                setCycleData(cycleDataMap);

                // Set current cycle to next available
                if (completedSet.size > 0) {
                    const maxCycle = Math.max(...Array.from(completedSet));
                    const nextCycle = maxCycle + 1;
                    if (nextCycle <= 8) {
                        console.log(`Max completed cycle: ${maxCycle}, setting current cycle to: ${nextCycle}`);
                        setCurrentCycle(nextCycle);
                        setIsExpanded(prev => ({ ...prev, [nextCycle]: true }));
                    }
                }
            } else {
                console.log('No cycles found, clearing completed cycles');
                setCompletedCycles(new Set());
                setCycleData({});
            }
        } catch (error) {
            console.error('Error fetching cycles:', error);
        } finally {
            // setIsLoadingCycles(false);
        }
    };


    useEffect(() => {
        if (isOfflineStarted && fetchedCycles.length > 0) {
            console.log('Processing offline data from Redux slice:', fetchedCycles);
            const newCycleData: { [key: number]: CycleProductData } = {};
            fetchedCycles.forEach((cycle) => {
                const cycleNum = parseInt(cycle.cycleNum);
                if (cycleNum) {
                    const convertParam = (value: string | { standardValue: string; actualValue: string; actionTaken: string }) => {
                        if (typeof value === 'string') {
                            return { standardValue: '', actualValue: value, actionTaken: '' };
                        }
                        return value;
                    };
                    const savedCycleData: CycleProductData = {
                        productName: cycle.product || 'N/A',
                        batchNo: '',
                        category: '',
                        lineNo: '',
                        executiveName: cycle.executiveName || 'N/A',
                        sku: '',
                        parameters: {
                            rpoSolidFat: convertParam(cycle.parameters.rpoSolidFat),
                            invertSyrup: convertParam(cycle.parameters.invertSyrup),
                            butter: convertParam(cycle.parameters.butter),
                            blackjack: convertParam(cycle.parameters.blackjack),
                            water: convertParam(cycle.parameters.water),
                            creamingTime: convertParam(cycle.parameters.creamingTime),
                            mixingTime: convertParam(cycle.parameters.mixingTime),
                            doughTemperature: convertParam(cycle.parameters.doughTemperature),
                            standingTime: convertParam(cycle.parameters.standingTime),
                            biscuitAppearance: convertParam(cycle.parameters.biscuitAppearance),
                            stackHeight: convertParam(cycle.parameters.stackHeight),
                            dimensions: convertParam(cycle.parameters.dimensions),
                            moulderRpm: convertParam(cycle.parameters.moulderRpm),
                            sugarSaltCashewSprinkling: convertParam(cycle.parameters.sugarSaltCashewSprinkling),
                            ingredientsOfBatch: convertParam(cycle.parameters.ingredientsOfBatch),
                            moistureContent: { standardValue: '', actualValue: '', actionTaken: '' },
                            dryBiscuitWeight: { standardValue: '', actualValue: '', actionTaken: '' }
                        }
                    };
                    newCycleData[cycleNum] = savedCycleData;
                }
            });
            setCompletedCycles(prev => new Set([...prev, ...Object.keys(newCycleData).map(Number)]));
            setCycleData(prev => ({ ...prev, ...newCycleData }));
        }
    }, [fetchedCycles, isOfflineStarted]);

    // Process offline submissions from category (one product per cycle)
    useEffect(() => {
        if (isOfflineStarted) {
            const offlineData = offlineSubmissionsByCategory['Process Parameter Verification'] || [];
            offlineData.forEach((submission: any) => {
                const cycleNum = submission.cycleNo;
                const records = submission.records || [];
                const record = records[0];
                if (!record) return;
                const toParam = (v: string) => ({ standardValue: '', actualValue: v || '', actionTaken: '' });
                const savedCycleData: CycleProductData = {
                        productName: record.cr3ea_productname || 'N/A',
                        batchNo: '',
                        category: '',
                        lineNo: '',
                        executiveName: record.cr3ea_executivename || 'N/A',
                        sku: '',
                        parameters: {
                        rpoSolidFat: toParam(record.cr3ea_rposolidfat_temperature),
                        invertSyrup: toParam(record.cr3ea_invertsyrup_temperature),
                        butter: toParam(record.cr3ea_butter_temperature),
                        blackjack: toParam(record.cr3ea_blackjack_temperature),
                        water: toParam(record.cr3ea_water_temperature),
                        creamingTime: toParam(record.cr3ea_creamingtime_temperature),
                        mixingTime: toParam(record.cr3ea_mixingtime_temperature),
                        doughTemperature: toParam(record.cr3ea_doughtemperature_temperature),
                        standingTime: toParam(record.cr3ea_standingtime_temperature),
                        biscuitAppearance: toParam(record.cr3ea_biscuitappearance_temperature),
                        stackHeight: toParam(record.cr3ea_stackheightspecifiednobiscuits_temperature),
                        dimensions: toParam(record.cr3ea_dimensionsatpackingtable_temperature),
                        moulderRpm: toParam(record.cr3ea_moulderrpmstokes_temperature),
                        sugarSaltCashewSprinkling: toParam(record.cr3ea_sugarsaltcashewsprinkling_temperature),
                        ingredientsOfBatch: toParam(record.cr3ea_ingredientsofbatch_temperature),
                        moistureContent: toParam(''),
                        dryBiscuitWeight: toParam('')
                        }
                    };
                    setCompletedCycles(prev => new Set([...prev, cycleNum]));
                    setCycleData(prev => ({ ...prev, [cycleNum]: savedCycleData }));
            });
        }
    }, [offlineSubmissionsByCategory, isOfflineStarted]);

    // Set current cycle ONLY on initial mount when we have persisted data (do NOT run when user saves a product)
    useEffect(() => {
        if (completedCycles.size === 0) {
                setCurrentCycle(1);
            return;
        }
        const maxCompletedCycle = Math.max(...Array.from(completedCycles));
        const nextCycleNumber = Math.min(maxCompletedCycle + 1, 8);
        setCurrentCycle(nextCycleNumber);
        console.log(`Mount: ${completedCycles.size} completed cycles. Setting current to: ${nextCycleNumber}`);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

    // Fetch cycles on mount (similar to QualityWallRecord)
    useEffect(() => {
        if (plantTourId && !isOfflineStarted) {
            fetchAndDisplayCycles();
        }
    }, [plantTourId, isOfflineStarted]);

    // Get current date in DD/MM/YYYY format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    const nextCycleNum = currentCycle + 1;

    return (
        <DashboardLayout>
            {/* Header Section with Back Button and Plant Tour ID */}
            <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 border-b border-gray-200 w-full">
                <div className="flex items-center justify-between gap-2">
                    {/* Back Button */}
                    <button
                        onClick={() => {
                            if (window.history.length > 1) {
                                navigate(-1);
                            } else {
                                navigate('/home');
                            }
                        }}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                    >
                        <span className="text-lg mr-1">&lt;</span>
                        <span className="font-medium text-sm sm:text-base">Back</span>
                    </button>

                    {/* Plant Tour ID */}
                    <div className="text-right min-w-0 flex-1">
                        <span className="text-gray-700 text-sm sm:text-base">Plant Tour ID: </span>
                        <span className="text-blue-600 font-medium text-sm sm:text-base break-all truncate" title={plantTourId || 'N/A'}>
                            {plantTourId || 'N/A'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="w-full px-3 sm:px-4 md:px-6 pb-6">
                {/* Main Header Bar */}
                <div className="bg-gray-100 rounded-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-2">
                                <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">Mixing Parameter Verification</h1>
                                <span className="text-red-600 font-medium text-xs sm:text-sm">(Every 2 hrs)</span>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                                <span className="text-gray-500">•</span>
                                <div className="bg-gray-200 rounded-full px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-2">
                                    <span className="text-xs sm:text-sm font-medium text-gray-700">{selectedCycle || 'Shift 1'}</span>
                                </div>
                                <span className="text-gray-500">•</span>
                                <span className="text-xs sm:text-sm text-gray-600">{formattedDate}</span>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <button className="text-gray-500 hover:text-gray-700 p-1">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Completed Cycles Display - One product per cycle */}
                {Array.from(completedCycles).sort((a, b) => a - b).filter(cycleNum => cycleNum !== currentCycle).map(cycleNum => {
                    const data = cycleData[cycleNum];
                    const isExpandedCycle = expandedCompletedCycles.has(cycleNum.toString());
                    if (!data || !data.parameters) return null;
                    return (
                        <div key={cycleNum} className="w-full mb-4">
                            <div className="bg-white rounded-t-lg border border-gray-200 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-1">Cycle {cycleNum}</h2>
                                        {isExpandedCycle && (
                                            <h3 className="text-sm font-semibold mt-1" style={{ color: '#dc2626', fontSize: '14px', fontWeight: 600 }}>Cycle Summary - All Parameters</h3>
                                        )}
                                    </div>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 cursor-pointer transition-transform ${isExpandedCycle ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        onClick={() => toggleCompletedCycleExpansion(cycleNum.toString())}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            
                            {isExpandedCycle && (
                                <div className="mb-4 last:mb-0">
                                    <div className="px-3 py-2 bg-blue-50 border-b border-gray-200 font-semibold text-blue-800 text-sm">
                                        {data.productName || 'N/A'}
                                    </div>
                                <div className="bg-white rounded-b-lg border-l border-r border-b border-gray-200 p-4 shadow-sm">
                                    <div className="overflow-x-auto">
                                        <table className="w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ 
                                                        textAlign: 'left', 
                                                        padding: '12px 16px', 
                                                        fontSize: '14px', 
                                                        fontWeight: 600, 
                                                        color: '#ffffff',
                                                        backgroundColor: '#9333ea',
                                                        borderBottom: '1px solid #d1d5db',
                                                        borderRight: '1px solid #d1d5db'
                                                    }}>Parameter</th>
                                                    <th style={{ 
                                                        textAlign: 'left', 
                                                        padding: '12px 16px', 
                                                        fontSize: '14px', 
                                                        fontWeight: 600, 
                                                        color: '#ffffff',
                                                        backgroundColor: '#9333ea',
                                                        borderBottom: '1px solid #d1d5db',
                                                        borderRight: '1px solid #d1d5db'
                                                    }}>Standard</th>
                                                    <th style={{ 
                                                        textAlign: 'left', 
                                                        padding: '12px 16px', 
                                                        fontSize: '14px', 
                                                        fontWeight: 600, 
                                                        color: '#ffffff',
                                                        backgroundColor: '#9333ea',
                                                        borderBottom: '1px solid #d1d5db',
                                                        borderRight: '1px solid #d1d5db'
                                                    }}>Actual (Temperature/Time/M.C%)</th>
                                                    <th style={{ 
                                                        textAlign: 'left', 
                                                        padding: '12px 16px', 
                                                        fontSize: '14px', 
                                                        fontWeight: 600, 
                                                        color: '#ffffff',
                                                        backgroundColor: '#9333ea',
                                                        borderBottom: '1px solid #d1d5db'
                                                    }}>Action Taken</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const getParamValue = (key: string): { standard: string; actual: string; action: string } => {
                                                        const param = data.parameters[key];
                                                        if (!param) return { standard: '', actual: '', action: '' };
                                                        if (typeof param === 'object' && param.standardValue !== undefined) {
                                                            return {
                                                                standard: String(param.standardValue || 'As per standard'),
                                                                actual: String(param.actualValue || ''),
                                                                action: String(param.actionTaken || '-')
                                                            };
                                                        }
                                                        return { standard: 'As per standard', actual: String(param || ''), action: '-' };
                                                    };

                                                    const parameters = [
                                                        { key: 'rpoSolidFat', label: 'RPO/SOLID FAT' },
                                                        { key: 'invertSyrup', label: 'Invert Syrup' },
                                                        { key: 'butter', label: 'Butter' },
                                                        { key: 'blackjack', label: 'Blackjack' },
                                                        { key: 'water', label: 'Water' },
                                                        { key: 'creamingTime', label: 'Creaming Time' },
                                                        { key: 'mixingTime', label: 'Mixing Time' },
                                                        { key: 'doughTemperature', label: 'Dough Temperature' },
                                                        { key: 'standingTime', label: 'Standing Time' },
                                                        { key: 'biscuitAppearance', label: 'Biscuit Appearance' },
                                                        { key: 'stackHeight', label: 'Stack Height/Specified no. of biscuits' },
                                                        { key: 'dimensions', label: 'Dimensions (at packing table)' },
                                                        { key: 'moistureContent', label: 'Moisture Content' },
                                                        { key: 'dryBiscuitWeight', label: 'Dry Biscuit weight/Specified no. of biscuits' },
                                                        { key: 'moulderRpm', label: 'Moulder RPM/STOKES' },
                                                        { key: 'sugarSaltCashewSprinkling', label: 'Sugar/Salt/Cashew Sprinkling' },
                                                        { key: 'ingredientsOfBatch', label: 'Ingredients of batch' }
                                                    ];

                                                    return parameters.map((param) => {
                                                        const values = getParamValue(param.key);
                                                        return (
                                                            <tr key={param.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={{ 
                                                                    textAlign: 'left', 
                                                                    padding: '12px 16px', 
                                                                    fontSize: '14px', 
                                                                    color: '#000000',
                                                                    backgroundColor: '#ffffff',
                                                                    borderRight: '1px solid #d1d5db'
                                                                }}>{param.label}</td>
                                                                <td style={{ 
                                                                    textAlign: 'left', 
                                                                    padding: '12px 16px', 
                                                                    fontSize: '14px', 
                                                                    color: '#000000',
                                                                    backgroundColor: '#ffffff',
                                                                    borderRight: '1px solid #d1d5db'
                                                                }}>{values.standard || 'As per standard'}</td>
                                                                <td style={{ 
                                                                    textAlign: 'left', 
                                                                    padding: '12px 16px', 
                                                                    fontSize: '14px', 
                                                                    color: '#000000',
                                                                    backgroundColor: '#fef9c3',
                                                                    borderRight: '1px solid #d1d5db'
                                                                }}>{values.actual || '-'}</td>
                                                                <td style={{ 
                                                                    textAlign: 'left', 
                                                                    padding: '12px 16px', 
                                                                    fontSize: '14px', 
                                                                    color: '#000000',
                                                                    backgroundColor: '#ffffff'
                                                                }}>{values.action || '-'}</td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Main Content - Current Cycle Section */}
                {currentCycle <= totalCycles && (
                <div className={`rounded-lg shadow-sm border w-full mb-6 ${
                    completedCycles.has(currentCycle) 
                        ? 'border-gray-200' 
                        : 'bg-white border-gray-200'
                }`}>
                    {/* Start Session Form */}
                    {!isSessionStarted && (
                        <div className="p-3 sm:p-4 md:p-6">
                            {/* Cycle Title */}
                            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 sm:mb-6">Cycle {currentCycle}</h2>
                            
                            {/* Two Input Fields Side by Side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                                {/* Product Name Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                                    <input
                                        type="text"
                                        value={startFormData.productName}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, productName: e.target.value }))}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter Product Name"
                                    />
                                </div>

                                {/* Executive Name Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Executive Name</label>
                                    <input
                                        type="text"
                                        value={startFormData.executiveName}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, executiveName: e.target.value }))}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter Executive Name"
                                    />
                                </div>
                            </div>

                            {/* Start Session Button - Bottom Right */}
                            <div className="flex justify-end">
                                <button
                                    onClick={handleStartSession}
                                    className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium"
                                >
                                    Start Session
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Session Started Content - Save Section */}
                    {isSessionStarted && (
                        <div className="p-4 sm:p-6 space-y-6">
                            {/* Cycle Header with Dropdown */}
                            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                                <h2 className="text-base sm:text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                                <svg
                                    className={`w-5 h-5 text-gray-600 cursor-pointer transition-transform ${isExpanded[currentCycle] ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    onClick={() => setIsExpanded(prev => ({ ...prev, [currentCycle]: !prev[currentCycle] }))}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>

                            {/* Header: Product Name, Executive Name, Baking Time (default: current time, manually editable) */}
                            {(() => {
                                const storageKey = `process-parameter-cycle-${currentCycle}-start-data`;
                                const stored = localStorage.getItem(storageKey);
                                const parsed = stored ? (() => { try { return JSON.parse(stored); } catch { return {}; } })() : {};
                                const bakingStart = sessionStartTime || (parsed.bakingStartTime ? new Date(parsed.bakingStartTime) : null);
                                const defaultTimeStr = bakingStart
                                    ? `${String(bakingStart.getMinutes()).padStart(2, '0')}:${String(bakingStart.getSeconds()).padStart(2, '0')}`
                                    : '';
                                const inputValue = bakingTimeManual !== '' ? bakingTimeManual : defaultTimeStr;
                                const handleBakingTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = formatTimeInputLive(e.target.value);
                                    setBakingTimeManual(v);
                                    try {
                                        const existing = localStorage.getItem(storageKey);
                                        const data = existing ? JSON.parse(existing) : {};
                                        localStorage.setItem(storageKey, JSON.stringify({ ...data, bakingTimeManual: v }));
                                    } catch { /* ignore */ }
                                };
                                return (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <div className="text-xs sm:text-sm text-gray-600 mb-0.5">Product Name</div>
                                                <div className="text-sm font-semibold text-gray-800 truncate">{startFormData.productName || '–'}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs sm:text-sm text-gray-600 mb-0.5">Executive Name</div>
                                                <div className="text-sm font-semibold text-gray-800 truncate">{startFormData.executiveName || '–'}</div>
                                            </div>
                                            <div>
                                                <label htmlFor="mixing-baking-time" className="text-xs sm:text-sm text-gray-600 mb-0.5 block">Baking Time (minutes:seconds)</label>
                                                <input
                                                    id="mixing-baking-time"
                                                    type="text"
                                                    value={inputValue}
                                                    onChange={handleBakingTimeChange}
                                                    onBlur={(e) => {
                                                        const formatted = formatTimeMMSS(e.target.value);
                                                        if (formatted !== e.target.value) {
                                                            setBakingTimeManual(formatted);
                                                            try {
                                                                const existing = localStorage.getItem(storageKey);
                                                                const data = existing ? JSON.parse(existing) : {};
                                                                localStorage.setItem(storageKey, JSON.stringify({ ...data, bakingTimeManual: formatted }));
                                                            } catch { /* ignore */ }
                                                        }
                                                    }}
                                                    placeholder="MM:SS"
                                                    maxLength={5}
                                                    aria-label="Baking time in minutes and seconds"
                                                    className="w-full min-w-[100px] text-base font-bold text-gray-900 border-2 border-gray-400 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-text"
                                                    style={{ minHeight: '2.5rem' }}
                                                    title="Enter minutes and seconds (e.g. 03:10). Default is session start time."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Section 1: Premixing - Ingredient Temperature °C */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Premixing - Ingredient Temperature °C</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Twice in a shift)</p>
                                
                                {/* RPO/SOLID FAT */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">RPO/SOLID FAT</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.rpoSolidFat?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('rpoSolidFat', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.rpoSolidFat?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('rpoSolidFat', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.rpoSolidFat?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('rpoSolidFat', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Invert Syrup */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Invert Syrup</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.invertSyrup?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('invertSyrup', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.invertSyrup?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('invertSyrup', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.invertSyrup?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('invertSyrup', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Butter */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Butter</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.butter?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('butter', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.butter?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('butter', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.butter?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('butter', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Blackjack */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Blackjack</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.blackjack?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('blackjack', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.blackjack?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('blackjack', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.blackjack?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('blackjack', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Water */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Water</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.water?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('water', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.water?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('water', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.water?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('water', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 1.5: Sieving */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Sieving</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Every 2 hrs)</p>
                                
                                {/* Ingredients of batch */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Ingredients of batch</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.ingredientsOfBatch?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('ingredientsOfBatch', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                                            <input
                                                type="text"
                                                value={processParameters.ingredientsOfBatch?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('ingredientsOfBatch', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.ingredientsOfBatch?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('ingredientsOfBatch', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Mixing Parameters */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Mixing Parameters</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Every 2 hrs)</p>
                                
                                {/* Creaming Time */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Creaming Time</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.creamingTime?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('creamingTime', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.creamingTime?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('creamingTime', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('creamingTime', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.creamingTime?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('creamingTime', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Mixing Time */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Mixing Time</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.mixingTime?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('mixingTime', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.mixingTime?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('mixingTime', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('mixingTime', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.mixingTime?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('mixingTime', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Dough Temperature */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Dough Temperature</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.doughTemperature?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('doughTemperature', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.doughTemperature?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('doughTemperature', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('doughTemperature', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.doughTemperature?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('doughTemperature', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Standing Time */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Standing Time</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.standingTime?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('standingTime', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.standingTime?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('standingTime', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('standingTime', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.standingTime?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('standingTime', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2.5: Forming */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Forming</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Every 2 hrs)</p>
                                
                                {/* Moulder RPM/Strokes */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Moulder RPM/Strokes</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.moulderRpm?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('moulderRpm', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.moulderRpm?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('moulderRpm', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('moulderRpm', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.moulderRpm?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('moulderRpm', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Sugar/Salt/Cashew Sprinkling */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Sugar/Salt/Cashew Sprinkling</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.sugarSaltCashewSprinkling?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('sugarSaltCashewSprinkling', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.sugarSaltCashewSprinkling?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('sugarSaltCashewSprinkling', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('sugarSaltCashewSprinkling', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.sugarSaltCashewSprinkling?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('sugarSaltCashewSprinkling', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Biscuit Parameters */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Biscuit Parameters</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Every 2 hrs)</p>
                                
                                {/* Biscuit Appearance */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Biscuit Appearance</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.biscuitAppearance?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('biscuitAppearance', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.biscuitAppearance?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('biscuitAppearance', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('biscuitAppearance', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.biscuitAppearance?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('biscuitAppearance', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Stack Height/Specified no. of biscuits */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Stack Height/Specified No. of biscuits</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.stackHeight?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('stackHeight', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.stackHeight?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('stackHeight', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('stackHeight', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.stackHeight?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('stackHeight', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Dimensions */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Dimensions</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.dimensions?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('dimensions', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Time (MM:SS)</label>
                                            <input
                                                type="text"
                                                value={processParameters.dimensions?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('dimensions', 'actualValue', formatTimeInputLive(e.target.value))}
                                                onBlur={(e) => {
                                                    const formatted = formatTimeMMSS(e.target.value);
                                                    if (formatted !== e.target.value) handleParameterChange('dimensions', 'actualValue', formatted);
                                                }}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="MM:SS"
                                                maxLength={5}
                                                title="Minutes and seconds (e.g. 03:10)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.dimensions?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('dimensions', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Moisture Content */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Moisture Content</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Sample to be taken at stacker) - Every 1 Hour</p>
                                
                                {/* Moisture Content */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Moisture Content</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.moistureContent?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('moistureContent', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">M.C%</label>
                                            <input
                                                type="text"
                                                value={processParameters.moistureContent?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('moistureContent', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.moistureContent?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('moistureContent', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: Dry Biscuit weight/Specified no. of biscuits */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-1">Dry Biscuit weight/Specified no. of biscuits</h3>
                                <p className="text-xs text-red-600 font-medium mb-4">(Every Hour)</p>
                                
                                {/* Dry Biscuit weight/Specified no. of biscuits */}
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Dry Biscuit weight/Specified no. of biscuits</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Standard</label>
                                            <input
                                                type="text"
                                                value={processParameters.dryBiscuitWeight?.standardValue || ''}
                                                onChange={(e) => handleParameterChange('dryBiscuitWeight', 'standardValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Standard value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">M.C%</label>
                                            <input
                                                type="text"
                                                value={processParameters.dryBiscuitWeight?.actualValue || ''}
                                                onChange={(e) => handleParameterChange('dryBiscuitWeight', 'actualValue', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Actual value"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Action taken in case of deviation</label>
                                            <div className="relative">
                                                <textarea
                                                    value={processParameters.dryBiscuitWeight?.actionTaken || ''}
                                                    onChange={(e) => handleParameterChange('dryBiscuitWeight', 'actionTaken', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                                                    style={{ minHeight: '2.5rem' }}
                                                    placeholder="Action if deviation..."
                                                />
                                                <svg className="w-3 h-3 text-gray-400 absolute bottom-1.5 right-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M7 7h10v10" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-3 justify-end pt-4">
                                <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveSession}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                    Save Product Checklist
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* Next Cycle (Disabled) - Show if current cycle is active (not completed) */}
                {nextCycleNum <= totalCycles && !completedCycles.has(currentCycle) && !completedCycles.has(nextCycleNum) && (
                    <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-300 p-3 sm:p-4 md:p-6 w-full opacity-60 cursor-not-allowed mb-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="text-base sm:text-lg font-bold text-gray-500">Cycle {nextCycleNum}</h2>
                            </div>
                            <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 cursor-not-allowed"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default MixingParameterVerification;

